import type { Observable } from 'rxjs'
import { merge, of } from 'rxjs'
import { flatMap, map, reduce, take } from 'rxjs/operators'
import type { Unit } from '../Unit'
import type { FieldsValue } from '../formBuilder'
import type { Validator } from './Validator'
import type { ValidatorMapResult, ValidatorObservableSubjectContextualized } from './ValidatorResult'
import { ValidationResultType } from './ValidatorResult'

export class ValidatorRunner {
    constructor(readonly validators: Validator.validatorsBuilded) {}

    private prepareValidators(
        value: Unit.UnionFields['v'],
        data: Unit.UnionFields['d'],
        fields: FieldsValue,
        async: boolean
    ) {
        return Object.keys(this.validators)
            .filter(validatorName => this.validators[validatorName].validator.isAsync === async)
            .map(validatorName => {
                const validatorsToObs = (validator: Validator.validatorsBuilded['string']) => {
                    switch (validator.type) {
                        case 'single':
                            return validator.validator.validate(value, data)
                        case 'multiple':
                            return validator.validator.validate(fields)
                    }
                }
                return validatorsToObs(this.validators[validatorName]).pipe(
                    map(
                        (result): ValidatorObservableSubjectContextualized => ({
                            result: result,
                            name: validatorName,
                        })
                    ),
                    take(1)
                )
            })
    }

    private run(
        value: Unit.UnionFields['v'],
        data: Unit.UnionFields['d'],
        fields: FieldsValue,
        async: boolean
    ): Observable<ValidatorMapResult> {
        return merge(...this.prepareValidators(value, data, fields, async)).pipe(
            reduce<ValidatorObservableSubjectContextualized, ValidatorMapResult>(
                (c, r) => ({ ...c, [r.name]: r.result }),
                {}
            )
        )
    }

    validate(
        value: Unit.UnionFields['v'],
        data: Unit.UnionFields['d'],
        fields: FieldsValue
    ): Observable<ValidatorMapResult> {
        return this.run(value, data, fields, false).pipe(
            flatMap(rSync =>
                Object.values(rSync).filter(r => r.type === ValidationResultType.ERROR).length === 0
                    ? this.run(value, data, fields, true).pipe(map(rAsync => ({ ...rSync, ...rAsync })))
                    : of(rSync)
            )
        )
    }
}
